<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import AceEditor from 'vue2-ace-editor'
import Card from '../components/slds/Card'
import Input from '../components/slds/Input'
import TabSet from '../components/slds/tabs/TabSet'
import Tab from '../components/slds/tabs/Tab'

export default {
  props: {
    id: String
  },
  components: {
    AceEditor,
    Card,
    Input,
    Tab,
    TabSet
  },
  data () {
    return {
      campaignId: '',
      campaign: '',
      config: '',
      offersTree: {},
      readonlyFields: [
        'dataExtensionName',
        'onlyHeader',
        'matExternalId',
        'relevantContribution'
      ]
    }
  },
  async created () {
    if (this.id) {
      this.campaignId = this.id
      await this.search()
    }
  },
  methods: {
    ...mapActions({
      getCampaign: 'campaignDesigner/getCampaign',
      update: 'campaignDesigner/overwriteCampaign'
    }),
    editorInit () {
      require('brace/ext/language_tools')
      require('brace/mode/json')
      require('brace/theme/monokai')
    },
    prettify (value) {
      return JSON.stringify(value, null, 2)
    },
    async search () {
      this.$router.replace(`/clean-it/${this.campaignId}`)
      this.campaign = {}
      const campaign = await this.getCampaign(this.campaignId)
      if (campaign.id) {
        this.campaign = campaign
        this.config = _.cloneDeep(this.prettify(campaign.config))

        if (campaign.recordType === 'full') {
          this.offersTree = campaign.config.offers.reduce((acc, val) => {
            Object.entries(val).forEach(([k, v]) => {
              if (!acc[k]) {
                acc[k] = v
              } else {
                acc[k].push(v)
              }
            })
            return acc
          }, {})
        }
      } else {
        console.error(`Campaign with id ${this.campaignId} not found`)
        this.showToast('error', this.$t('general.error'), 'text', `Campaign with id ${this.campaignId} not found`)
      }
    },
    async save () {
      try {
        this.campaign.config = JSON.parse(this.config)
        await this.update({ id: this.campaignId, campaign: this.campaign })
        this.showToast('success', this.$t('general.success'), 'text', this.$t('message.update'))
        await this.search()
      } catch (error) {
        this.showToast('error', this.$t('general.error'), 'text', error)
        console.error(error)
      }
    }
  }
}
</script>
<template>
  <div>
    <card title="Select campaign">
      <template #actions>
        <button class="slds-button slds-button_brand" :disabled="!campaignId" @click="search">Search</button>
      </template>
      <Input label="Campaign ID" v-model="campaignId" @keyup-enter="search" />
    </card>

    <card :title="`Campaign ${campaign.matExternalId}`" v-if="campaign.id">
      <template #actions>
        <button class="slds-button slds-button_brand" :disabled="!campaignId" @click="save">Save</button>
      </template>

      <card class="slds-card_boundary" title="Config">
        <tab-set tab-set-id="config">
          <tab label="JSON" tab-set-id="config" position="0">
            <ace-editor
              @init="editorInit"
              v-model="config"
              height="300"
              lang="json"
              theme="monokai"
              width="100%" />
          </tab>
          <!-- CC / Full -->
          <tab label="Offers" tab-set-id="config" v-if="campaign.recordType === 'full'" position="1">
            <card v-for="offer in Object.keys(offersTree)" :title="offer" class="slds-card_boundary" :key="offer">
              <card class="slds-card_boundary" :title="section.sectionId" v-for="section in offersTree[offer]" :key="section.sectionId">
                <div class="slds-form" role="list">
                  <div class="slds-form__row" v-for="[field, value] in Object.entries(section)" :key="field">
                    <div class="slds-form__item" role="listitem">
                      <div class="slds-form-element slds-form-element_horizontal slds-is-editing">
                        <label class="slds-form-element__label">
                          {{field}}
                        </label>
                        <div class="slds-form-element__control">
                          <span class="slds-text-font_monospace">{{value}}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </card>
            </card>
          </tab>
          <tab label="Sections" tab-set-id="config" v-if="campaign.recordType === 'full'" position="2">
            <tab-set tab-set-id="filters">
              <tab v-for="([id, filters], index) in Object.entries(campaign.config.filters)" tab-set-id="filters" :position="`${index}`" :label="id" :key="id">
                <div class="slds-form" role="list">
                  <div class="slds-form__row" v-for="[field, value] in Object.entries(filters)" :key="field">
                    <div class="slds-form__item" role="listitem">
                      <div class="slds-form-element slds-form-element_horizontal slds-is-editing">
                        <label class="slds-form-element__label">
                          {{field}}
                        </label>
                        <div class="slds-form-element__control">
                          <span class="slds-text-font_monospace">{{value}}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </tab>
            </tab-set>
          </tab>
        </tab-set>
      </card>

      <card class="slds-card_boundary" title="Campaign fields">
        <div class="slds-form" role="list">
          <div class="slds-form__row" v-for="field in Object.keys(campaign)" :key="field">
            <div class="slds-form__item" role="listitem" v-if="field !== 'id' && field !== 'config'">
              <div class="slds-form-element slds-form-element_horizontal slds-is-editing">
                <label class="slds-form-element__label">
                  {{field}}
                </label>
                <div class="slds-form-element__control">
                  <template v-if="field !== 'query' && field !== 'queryPreview' && field !== 'preview'">
                    <input type="text" class="slds-input slds-text-font_monospace" v-model="campaign[field]" :readonly="readonlyFields.includes(field)" />
                  </template>
                  <template v-else-if="field === 'preview'">
                    <textarea class="slds-textarea slds-text-font_monospace" readonly :value="JSON.stringify(campaign[field])" />
                  </template>
                  <template v-else>
                     <textarea class="slds-textarea slds-text-font_monospace" v-model="campaign[field]" />
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </card>
    </card>
  </div>
</template>
